import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  CircleIcon,
  RefreshCwIcon,
} from "lucide-react";
import React, {
  forwardRef,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useObjectRef } from "react-aria";
import { Autocomplete, useFilter } from "react-aria-components";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import * as z from "zod";

import { extendResult } from "@blis/shared/models/result";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { DetailItem } from "@/components/DetailItem";
import { Link } from "@/components/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import { Centered } from "@/components/ui/container";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import { Accordion } from "@/components/ui/legacy/Accordion";
import { DropdownItem, ListBox } from "@/components/ui/list-box";
import { Note } from "@/components/ui/note";
import { Popover } from "@/components/ui/popover";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Select, SelectTrigger } from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import { apiUtils } from "@/context/query";
import { BaseTestResultBadge } from "@/features/accession/components/dialogs/TestResultDetailsDialog";
import { UpdateSampleUsedDialog } from "@/features/accession/components/dialogs/UpdateSampleUsedDialog";
import { useFeatureFlags } from "@/features/admin/hooks";
import { useAuth } from "@/features/auth/hooks";
import { useConfig } from "@/lib/config";
import { usePrintLabel } from "@/lib/labels";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { format } from "@/lib/utils";
import { removeEmptyFields } from "@/utils/removeEmptyFields";
import { mutationResponse } from "@/utils/trpc";
import type { WorklistNew } from "../types";

type TestOrder = WorklistNew["testOrders"][0] & {
  reflexOrders?: WorklistNew["testOrders"];
};
type Accession = TestOrder["accession"];
type Result = TestOrder["results"][0];
type ResultType = Result["testResultSpec"]["type"];

const enumSchema = z.object({
  resultEnum: z.string().min(1, "Required"),
});
const numericSchema = z.object({
  resultNumeric: z.coerce.number<number>({ error: "Required" }),
});
const hlaSchema = z.object({
  resultHla: z
    .string()
    .min(1, "Required")
    .array()
    .refine((val) => {
      if (val.length === 1) {
        return val[0] === "Positive" || val[0] === "Negative";
      }
      return val.length === 2;
    }),
});
const freeTextSchema = z.object({
  resultFreeText: z.string().min(1, "Required"),
});

const resultsSchema = enumSchema
  .extend(numericSchema.shape)
  .extend(hlaSchema.shape)
  .extend(freeTextSchema.shape);

const enterSchema = z.object({
  id: z.number(),
  sampleId: z.number({ error: "Required" }),
  comment: z.string().optional(),
});

const validateSchema = z.object({ id: z.number() });

// biome-ignore lint/suspicious/noExplicitAny: dynamic schema map
const enterSchemaMap: Record<ResultType, z.ZodType<any, any>> = {
  TEXT: enterSchema.extend(enumSchema.shape),
  NUMERIC: enterSchema.extend(numericSchema.shape),
  HLA: enterSchema.extend(hlaSchema.shape),
  FREE_TEXT: enterSchema.extend(freeTextSchema.shape),
  ORGANISM: enterSchema.extend(freeTextSchema.shape),
};
// biome-ignore lint/suspicious/noExplicitAny: dynamic schema map
const validateSchemaMap: Record<ResultType, z.ZodType<any, any>> = {
  TEXT: validateSchema.extend(enumSchema.shape),
  NUMERIC: validateSchema.extend(numericSchema.shape),
  HLA: validateSchema.extend(hlaSchema.shape),
  FREE_TEXT: validateSchema.extend(freeTextSchema.shape),
  ORGANISM: validateSchema.extend(freeTextSchema.shape),
};
const allEnterSchemas = enterSchema.extend(resultsSchema.shape);

type FormValues = z.infer<typeof allEnterSchemas>;

export const ResultForm = forwardRef<
  HTMLDivElement,
  {
    order: TestOrder;
    onLastResultComplete?: () => void;
    startValue?: string;
    header?: ReactNode;
    className?: string;
    includeReflex?: boolean;
    autoFocus?: boolean;
  }
>(
  (
    {
      className,
      onLastResultComplete,
      startValue,
      includeReflex,
      header,
      order,
      autoFocus = true,
    },
    ref,
  ) => {
    const innerRef = useObjectRef(ref);
    const inputRef = useRef<HTMLInputElement>(null);
    const firstAccordionRef = useRef<HTMLButtonElement>(null);

    useTinyKeys(
      {
        ArrowRight: () => {
          const el = findFirstInput(innerRef.current);
          if (!el) {
            firstAccordionRef.current?.focus();
          } else {
            el.focus();
          }
        },
      },
      [],
      false,
    );

    const accession = order.accession;

    const testOrder = order;

    if (testOrder.processInstanceId) {
      return (
        <div className="flex flex-col gap-2 -mt-2 px-4">
          <Note intent="warning">
            This test order is part of a workflow. Use the test run interface to
            enter results.
          </Note>
          {testOrder.testRunId ? (
            <Link
              to="/runs/$id/workspace"
              params={{ id: testOrder.testRunId }}
              asButton
              variant="outline"
            >
              View test run
            </Link>
          ) : (
            <Link to="/runs" asButton>
              Create test run
            </Link>
          )}
        </div>
      );
    }

    return (
      <div
        className={cn("shrink-0 focus:outline-hidden", className)}
        ref={innerRef}
      >
        {header}

        {order.cancelled ? (
          <div className="flex flex-col gap-2">
            <Centered className="p-4 bg-muted rounded-md">
              <Text muted>Test order cancelled</Text>
            </Centered>
            <DetailItem title="Cancelled by">
              {order.cancelledBy?.username}
            </DetailItem>
            <DetailItem title="Cancelled at">
              {format(order.cancelledAt)}
            </DetailItem>
            <DetailItem title="Reason">
              <Text>{order.cancelledReason}</Text>
            </DetailItem>
          </div>
        ) : (
          <ResultsList
            testOrder={testOrder}
            accordionRef={firstAccordionRef}
            inputRef={inputRef}
            innerRef={innerRef}
            accession={accession}
            onLastResultComplete={onLastResultComplete}
            includeReflex={includeReflex}
            startValue={startValue}
            autoFocus={autoFocus}
          />
        )}
      </div>
    );
  },
);

function ResultsList({
  testOrder,
  accordionRef,
  innerRef,
  accession,
  includeReflex,
  onLastResultComplete,
  startValue,
  autoFocus = true,
}: {
  testOrder: TestOrder;
  accordionRef: RefObject<HTMLButtonElement | null>;
  innerRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  accession: TestOrder["accession"];
  onLastResultComplete?: () => void;
  includeReflex?: boolean;
  startValue?: string;
  autoFocus?: boolean;
}) {
  const { featureFlags } = useFeatureFlags();
  const [value, setValue] = useState(startValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const config = useConfig();

  const selectOpenInput = () => {
    const el = findFirstInput(containerRef.current);
    el?.focus();
  };

  const clear = trpc.results.clear.useMutation();

  const onClear = (id: number) => {
    clear.mutate(
      { id },
      mutationResponse({
        errorTitle: "Error clearing result",
        successMessage: "Result cleared",
        onSuccess(data) {
          if (data?.id) {
            selectOpenInput();
            setValue(data.id.toString());
          }
        },
      }),
    );
  };

  useEffect(() => {
    setValue(startValue);
  }, [startValue]);

  // pull out reflex results and sort by id (time added to db)
  const reflexResults =
    testOrder.reflexOrders
      ?.flatMap((o) => o.results)
      .sort((a, b) => a.id - b.id) || [];

  const results = testOrder.results
    .concat(includeReflex ? reflexResults : [])
    .map(extendResult);

  const shouldShowResult = useCallback(
    (result: (typeof results)[0]) => {
      if (result.testResultSpec.isCalculated || result.cancelled) {
        return false;
      }

      if (!result.$initial) {
        return true;
      }

      return (
        !result.validated &&
        !result.testResultSpec.disableDoubleEntry &&
        (result.enteredById !== user?.profile.id ||
          featureFlags?.allowSelfValidation)
      );
    },
    [featureFlags?.allowSelfValidation, user?.profile.id],
  );

  const goToNext = useCallback(() => {
    const curr = results.findIndex((r) => r.id.toString() === value);
    for (let i = curr + 1; i < results.length; i++) {
      const result = results[i];
      if (shouldShowResult(result)) {
        setValue(result?.id.toString());
        const el = findFirstInput(innerRef.current);
        if (el) {
          el.focus();
        }
        return;
      }
    }
    onLastResultComplete?.();
  }, [innerRef, onLastResultComplete, results, shouldShowResult, value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: change on value
  useEffect(() => {
    if (autoFocus || startValue) {
      selectOpenInput();
    }
  }, [value, autoFocus, startValue]);

  return (
    <Accordion
      type="single"
      value={value}
      defaultValue={startValue}
      ref={containerRef}
      onValueChange={setValue}
      onKeyDown={(evt) => {
        // this is so the accession list doesn't take over navigation
        if (evt.key === "ArrowDown" || evt.key === "ArrowUp") {
          evt.stopPropagation();
        }
      }}
    >
      {results.map((result, idx) => (
        <React.Fragment key={result.id}>
          <Accordion.Item
            key={result.id}
            value={result.id.toString()}
            className="result-item"
          >
            <Accordion.Trigger
              ref={idx === 0 ? accordionRef : null}
              className="min-w-0 flex gap-2"
            >
              {result.isReflex && (
                <span>
                  <RefreshCwIcon className="shrink-0 size-4 text-muted-foreground" />
                </span>
              )}
              <Text
                truncate
                className="pr-3 text-sm md:text-base"
                strikethrough={result.cancelled}
              >
                {result.testResultSpec?.name}
              </Text>
              <div className="flex items-center justify-end gap-1 flex-1 mr-3">
                {result.testResultSpec.isCalculated && (
                  <Badge color="gray">Calculated</Badge>
                )}
                {result.cancelled ? (
                  <Badge color="gray">Cancelled</Badge>
                ) : result.$initial ? (
                  <CheckCircle2Icon className="text-indigo-600 size-5" />
                ) : (
                  <CircleIcon className="text-muted-foreground size-5" />
                )}
                {result.cancelled ||
                result.testResultSpec.isCalculated ? null : result.validated ? (
                  <CheckCircle2Icon className="text-indigo-600 size-5" />
                ) : (
                  <CircleIcon className="text-muted-foreground size-5" />
                )}
              </div>
            </Accordion.Trigger>
            <Accordion.Content>
              {result.cancelled ? (
                <div className="flex flex-col gap-2">
                  <Centered className="p-4 bg-muted rounded-md">
                    <Text muted>Result cancelled</Text>
                  </Centered>
                  <DetailItem title="Cancelled by">
                    {result.cancelledBy?.username}
                  </DetailItem>
                  <DetailItem title="Cancelled at">
                    {format(result.cancelledAt)}
                  </DetailItem>
                  {result.cancelledReason && (
                    <DetailItem title="Reason">
                      <Text>{result.cancelledReason}</Text>
                    </DetailItem>
                  )}
                </div>
              ) : (
                <TodoItem checked={!!result.$initial}>
                  <Text bold size="3" className="pb-2">
                    {result.testResultSpec.isCalculated
                      ? "Calculated "
                      : "Enter "}
                    result
                  </Text>
                  {result.$initial ? (
                    <>
                      {result.enteredById === user?.profile.id ? (
                        <Text muted className="inline">
                          You entered{" "}
                          <BaseTestResultBadge
                            result={result.result || getDisplayedResult(result)}
                            units={result.testResultSpec.units}
                          />
                          {" - "}
                          {dayjs(result.enteredAt).fromNow()}
                        </Text>
                      ) : result.testResultSpec.isCalculated ? (
                        <Text muted className="inline">
                          Calculated{" "}
                          <BaseTestResultBadge
                            result={getDisplayedResult(result)}
                            units={result.testResultSpec.units}
                          />
                          {" - "}
                          {dayjs(result.enteredAt).fromNow()}
                        </Text>
                      ) : (
                        <Text muted>
                          {result.enteredBy
                            ? result.enteredBy.username
                            : result.enteredByInstrument
                              ? result.enteredByInstrument.model
                              : "unknown"}{" "}
                          entered {dayjs(result.enteredAt).fromNow()}
                        </Text>
                      )}
                      <div>
                        <ConfirmPrompt
                          danger
                          loading={clear.isPending}
                          title="Clear result"
                          message="Are you sure you want to clear this result?"
                          onConfirm={() => onClear(result.id)}
                        >
                          <Button size="sm" variant="outline">
                            Clear result
                          </Button>
                        </ConfirmPrompt>
                      </div>
                    </>
                  ) : result.testResultSpec.isCalculated ? (
                    <Text muted>Awaiting initial result for calculation</Text>
                  ) : (
                    <EnterResultForm
                      result={result}
                      accession={accession}
                      order={testOrder}
                      onComplete={goToNext}
                    />
                  )}
                </TodoItem>
              )}

              {!result.cancelled &&
                result.$initial &&
                !result.testResultSpec.isCalculated && (
                  <TodoItem checked={!!result.validated}>
                    <Text bold size="3" className="pb-2">
                      Validate result
                    </Text>

                    {result.validated &&
                    (result.validatedBy || result.enteredByInstrumentId) ? (
                      <div className="flex flex-col gap-2">
                        <Text muted>
                          {result.validatedById === user?.profile.id
                            ? "You"
                            : result.validatedBy?.username ||
                              result.enteredByInstrument?.model ||
                              "system"}{" "}
                          validated {dayjs(result.validatedAt).fromNow()}
                        </Text>
                        {result.testResultSpec.disableDoubleEntry && (
                          <Text muted className="italic">
                            Result does not require double entry
                          </Text>
                        )}
                      </div>
                    ) : result.enteredById === user?.profile.id &&
                      !featureFlags?.allowSelfValidation ? (
                      <Text muted>
                        Result must be validated by another user
                      </Text>
                    ) : (
                      <ValidateResultForm
                        result={result}
                        accession={accession}
                        onComplete={goToNext}
                      />
                    )}
                  </TodoItem>
                )}
              {config.enableSubjectSampleMigration &&
                result.sample &&
                result.$initial && (
                  <div className="border rounded p-2">
                    <Text bold size="3" className="pb-2" elementType="p">
                      Sample used
                    </Text>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" size="sm">
                        {result.sample.displayId}
                      </Badge>
                      <Text className="flex-1">{result.sample.tube.name}</Text>
                      <UpdateSampleUsedDialog
                        accessionId={accession.id}
                        result={result}
                      >
                        <Button variant="outline" size="sm">
                          Edit
                        </Button>
                      </UpdateSampleUsedDialog>
                    </div>
                  </div>
                )}
            </Accordion.Content>
          </Accordion.Item>
        </React.Fragment>
      ))}
    </Accordion>
  );
}

function EnterResultForm(props: {
  accession: Accession;
  result: Result;
  order: TestOrder;
  onComplete: () => void;
}) {
  const enter = trpc.results.enter.useMutation(
    mutationResponse({
      errorTitle: "Error entering result",
      successMessage: (data) => {
        if (data) {
          const display = data.result || getDisplayedResult(data);
          return (
            <>
              Result entered:{" "}
              <BaseTestResultBadge
                result={display}
                units={data.testResultSpec.units}
                noColor={data.testResultSpec.type === "HLA"}
              />
            </>
          );
        }
        return;
      },
      onSuccess() {
        props.onComplete();
      },
    }),
  );

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      await enter.mutateAsync(removeEmptyFields(values));
    } catch (_err) {
      // no-op: handled by mutationResponse
    }
  };
  return <InnerResultForm {...props} onSubmit={onSubmit} />;
}

function ValidateResultForm(props: {
  accession: Accession;
  result: Result;
  onComplete: () => void;
}) {
  const printReflexLabels = usePrintReflexLabels();

  const validate = trpc.results.validate.useMutation(
    mutationResponse({
      errorTitle: "Error validating result",
      successMessage: (data) => {
        if (data) {
          const display = data.result.result || getDisplayedResult(data.result);
          return (
            <>
              Result validated:{" "}
              <BaseTestResultBadge
                result={display}
                units={data.result.testResultSpec.units}
                noColor={data.result.testResultSpec.type === "HLA"}
              />
            </>
          );
        }
        return;
      },
      async onSuccess(data) {
        if (
          data?.shouldPrintReflexLabels &&
          data.result.sample &&
          data.reflexCount &&
          data.reflexStatus === "INITIAL"
        ) {
          await printReflexLabels(data.result.sample.id, data.reflexCount);
        }
        props.onComplete();
      },
    }),
  );

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      await validate.mutateAsync(values);
    } catch (_err) {
      // no-op: handled by mutationResponse
    }
  };
  return <InnerResultForm isValidation {...props} onSubmit={onSubmit} />;
}

function InnerResultForm({
  accession,
  result,
  onSubmit,
  order,
  isValidation,
}: {
  accession: TestOrder["accession"];
  result: TestOrder["results"][0];
  order?: TestOrder;
  onSubmit: SubmitHandler<FormValues>;
  isValidation?: boolean;
}) {
  const type = result.testResultSpec?.type || "TEXT";

  const config = useConfig();

  const recalc = trpc.results.calculate.useMutation(
    mutationResponse({
      successMessage: "Result calculated",
      errorTitle: "Error calculating result",
    }),
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      id: result.id,
      resultEnum: result.enteredByInstrument
        ? (result.resultId ?? undefined)
        : undefined,
      resultNumeric: result.enteredByInstrument
        ? (result.resultNumeric?.toNumber() ?? undefined)
        : undefined,
      resultHla: result.enteredByInstrument ? result.resultHla : undefined,
      sampleId: getDefaultSampleId({ accession, order, result }),
    },
    resolver: zodResolver(
      isValidation ? validateSchemaMap[type] : enterSchemaMap[type],
    ),
  });

  const isSubmitting = methods.formState.isSubmitting;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <input type="hidden" {...methods.register("id")} />
        <div className="flex flex-col gap-4">
          {type === "NUMERIC" ? (
            <NumericResultEntry result={result} accession={accession} />
          ) : type === "HLA" ? (
            <HlaResultEntry result={result} />
          ) : type === "FREE_TEXT" ? (
            <FreeTextResultEntry result={result} />
          ) : (
            <EnumResultSelect result={result} accession={accession} />
          )}
          {!isValidation && (
            <>
              {config.enableSubjectSampleMigration ? (
                <NewSampleSelect accession={accession} />
              ) : (
                <SampleSelect accession={accession} />
              )}
              <Controller
                control={methods.control}
                name="comment"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    id={`comment-${result.id}`}
                    validationBehavior="aria"
                    {...field}
                  >
                    <Label>Comment</Label>
                    <TextArea />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </>
          )}
          <div className="flex justify-end gap-2">
            {result.testResultSpec.isCalculated && (
              <Button
                type="button"
                onPress={() => {
                  recalc.mutate({ id: result.id });
                }}
                isPending={recalc.isPending}
                isDisabled={recalc.isPending || isSubmitting}
              >
                Calculate
              </Button>
            )}
            <Button
              type="submit"
              isPending={isSubmitting}
              isDisabled={recalc.isPending || isSubmitting}
            >
              {result.enteredByInstrument ? "Approve" : "Submit"}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function FreeTextResultEntry({ result }: { result: Result }) {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="resultFreeText"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <TextField {...field} validationBehavior="aria">
          <Label>Result</Label>
          <TextArea ref={ref} />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function EnumResultSelect({
  result,
  accession,
}: {
  result: Result;
  accession: Accession;
}) {
  const { control, watch } = useFormContext<FormValues>();
  const options =
    result.testResultSpec?.testResults.map((t) => ({
      value: t.testResult.value,
      description: t.testResult.description,
    })) || [];

  // Get the previous result for this test result spec and subject
  const { data: previousResult } = trpc.results.getPreviousForSubject.useQuery(
    {
      testResultSpecId: result.testResultSpec.id,
      subjectId: accession.subjectId,
      excludeResultId: result.id,
    },
    {
      enabled: !!accession.subjectId && !!result.testResultSpec.id,
    },
  );

  const selectedValue = watch("resultEnum");

  // Check if the selected value differs from the previous result
  const hasDifferentValue =
    previousResult &&
    selectedValue &&
    previousResult.result?.value !== selectedValue;

  return (
    <div className="flex flex-col gap-2">
      <Controller
        control={control}
        name="resultEnum"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <ComboBox
            {...field}
            aria-label="Result"
            menuTrigger="focus"
            onSelectionChange={(evt) => field.onChange(evt)}
            selectedKey={field.value ?? null}
            validationBehavior="aria"
            isInvalid={!!error}
          >
            <ComboBoxInput />
            <ComboBoxList items={options}>
              {(option) => (
                <ComboBoxItem key={option.value} id={option.value}>
                  {option.description}
                </ComboBoxItem>
              )}
            </ComboBoxList>
            <FieldError>{error?.message}</FieldError>
          </ComboBox>
        )}
      />
      {hasDifferentValue && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md">
          <AlertTriangleIcon className="size-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm flex flex-col gap-1">
            <Text className="text-amber-800 dark:text-amber-200 font-medium">
              Result differs from previous
            </Text>
            <Text className="text-amber-700 dark:text-amber-300" size="1">
              Previous result was{" "}
              <span className="font-medium">
                {previousResult.result?.description}
              </span>{" "}
              on {format(previousResult.accession?.date)}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

function NumericResultEntry(_props: { result: Result; accession: Accession }) {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="resultNumeric"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <TextField
          type="number"
          {...field}
          value={field.value?.toString() ?? ""}
          onChange={(value) => field.onChange(value)}
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Result</Label>
          <Input ref={ref} />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function HlaResultEntry({ result }: { result: Result }) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<FormValues>();

  // biome-ignore lint/suspicious/noExplicitAny: no types
  const resultOptions = ((result.testResultSpec?.options as any)
    ?.resultOptions || []) as string[];

  const options = resultOptions.map((value) => ({ value, label: value }));
  const currentValue = watch("resultHla");

  useEffect(() => {
    if (
      currentValue &&
      ["Positive", "Negative"].includes(currentValue[0]) &&
      currentValue.length > 1
    ) {
      setValue("resultHla", [currentValue[0]]);
    }
  }, [currentValue, setValue]);

  return (
    <div>
      <Label>Result</Label>
      <Controller
        control={control}
        name="resultHla"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <div className="flex gap-1">
            <ComboBox
              aria-label="HLA result 1"
              allowsCustomValue
              menuTrigger="focus"
              onInputChange={(evt) => {
                field.onChange([evt, currentValue?.[1]]);
              }}
              inputValue={field.value?.[0] ?? ""}
              validationBehavior="aria"
              onBlur={field.onBlur}
              name={field.name}
              isInvalid={!!error?.[0] || !!errors.resultHla}
            >
              <ComboBoxInput />
              <ComboBoxList items={options}>
                {(option) => (
                  <ComboBoxItem key={option.value} id={option.value}>
                    {option.label}
                  </ComboBoxItem>
                )}
              </ComboBoxList>
              <FieldError>
                {errors.resultHla?.[0]?.message || errors.resultHla?.message}
              </FieldError>
            </ComboBox>
            {/* <CreatableSelect<(typeof options)[0]>
              {...field}
              id={`result-${result.id}-result-0`}
              isClearable
              onChange={(evt) =>
                field.onChange([evt?.value, currentValue?.[1]])
              }
              value={
                field.value?.[0]
                  ? options.find((opt) => opt.value === field.value[0]) ?? {
                      label: field.value[0],
                      value: field.value[0],
                    }
                  : undefined
              }
              options={options}
              error={
                errors.resultHla?.[0]?.message || errors.resultHla?.message
              }
            /> */}
            {field.value?.[0] !== "Positive" &&
              field.value?.[0] !== "Negative" && (
                <>
                  <ComboBox
                    aria-label="HLA result 2"
                    allowsCustomValue
                    menuTrigger="focus"
                    onInputChange={(evt) => {
                      field.onChange([currentValue?.[0], evt]);
                    }}
                    inputValue={field.value?.[1] ?? ""}
                    validationBehavior="aria"
                    onBlur={field.onBlur}
                    name={field.name}
                    isInvalid={!!error?.[1] || !!errors.resultHla}
                  >
                    <ComboBoxInput />
                    <ComboBoxList items={options}>
                      {(option) => (
                        <ComboBoxItem key={option.value} id={option.value}>
                          {option.label}
                        </ComboBoxItem>
                      )}
                    </ComboBoxList>
                    <FieldError>
                      {errors.resultHla?.[1]?.message ||
                        errors.resultHla?.message}
                    </FieldError>
                  </ComboBox>
                  {/* <CreatableSelect<(typeof options)[0]>
                    {...field}
                    id={`result-${result.id}-result-1`}
                    isClearable
                    onChange={(evt) =>
                      field.onChange([currentValue?.[0], evt?.value])
                    }
                    value={
                      field.value?.[1]
                        ? options.find(
                            (opt) => opt.value === field.value[1],
                          ) ?? {
                            label: field.value[1],
                            value: field.value[1],
                          }
                        : undefined
                    }
                    options={options}
                    error={
                      errors.resultHla?.[1]?.message ||
                      errors.resultHla?.message
                    }
                  /> */}
                </>
              )}
          </div>
        )}
      />
    </div>
  );
}

function NewSampleSelect({ accession }: { accession: Accession }) {
  const { control } = useFormContext<FormValues>();
  const { contains } = useFilter({ sensitivity: "base" });
  const options = (accession.samples || [])
    .map((sample) => ({
      id: sample.id,
      displayId: sample.displayId || sample.id.toString(),
      tube: sample.tube,
      collection: sample.collection,
    }))
    .sort((a, b) => a.displayId.localeCompare(b.displayId));
  return (
    <Controller
      control={control}
      name="sampleId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          selectedKey={field.value ?? null}
          onSelectionChange={(key) => field.onChange(key)}
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Sample used</Label>
          <SelectTrigger className="truncate" isClearable />
          <Popover
            className="p-0  min-w-(--trigger-width) flex flex-col overflow-hidden"
            placement="bottom end"
          >
            <Autocomplete filter={contains}>
              <SearchField className="border-b" aria-label="Search samples">
                <SearchInput
                  className="border-none outline-none shadow-none rounded-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoFocus
                />
              </SearchField>
              <ListBox
                items={options}
                escapeKeyBehavior="none"
                renderEmptyState={() => (
                  <Text muted size="1" className="p-2">
                    No samples found
                  </Text>
                )}
                className="outline-hidden p-1 overflow-auto flex-1 scroll-pb-1"
              >
                {(item) => (
                  <DropdownItem
                    key={item.id}
                    id={item.id}
                    textValue={item.displayId}
                  >
                    <div className="flex items-center gap-2">
                      <Badge size="sm" variant="outline">
                        {item.displayId}
                      </Badge>
                      {item.tube?.name}
                    </div>
                  </DropdownItem>
                )}
              </ListBox>
            </Autocomplete>
          </Popover>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function SampleSelect({ accession }: { accession: Accession }) {
  const { control } = useFormContext<FormValues>();
  const options = accession.samples || [];

  return (
    <Controller
      control={control}
      name="sampleId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <ComboBox
          {...field}
          menuTrigger="focus"
          onSelectionChange={(evt) => field.onChange(evt)}
          selectedKey={field.value ?? null}
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Sample used</Label>
          <ComboBoxInput ref={ref} hideClear />
          <ComboBoxList items={options}>
            {(option) => (
              <ComboBoxItem
                key={option.id}
                id={option.id}
                textValue={option.tube?.name}
              >
                <Text slot="label">{option.tube?.name}</Text>
                <Description className="text-muted-foreground/80 text-xs">
                  {format(option.collection)}
                </Description>
              </ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}

function TodoItem({
  checked,
  children,
}: {
  checked?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex gap-2 pb-5">
      {checked ? (
        <CheckCircle2Icon className="size-5 text-indigo-600" />
      ) : (
        <CircleIcon className="size-5 text-muted-foreground" />
      )}
      <div className="flex flex-col w-full">{children}</div>
    </div>
  );
}

function getDefaultSampleId({
  accession,
  order,
  result,
}: {
  accession: Accession;
  result: Result;
  order?: TestOrder;
}) {
  const assignedSample = order?.samples[0]?.sampleId;
  const onlySample =
    accession.samples.length === 1 ? accession.samples[0].id : undefined;
  const preferredTestResultSpec = accession.samples.find(
    (s) => result.testResultSpec?.preferredTubeId === s.tube?.id,
  )?.id;
  const preferredTestSpec = accession.samples.find(
    (s) => order?.testSpec.preferredTubeId === s.tube?.id,
  )?.id;

  return (
    assignedSample ||
    onlySample ||
    preferredTestResultSpec ||
    preferredTestSpec ||
    undefined
  );
}

function getDisplayedResult(
  result?: Pick<Result, "result" | "resultNumeric" | "resultHla"> | null,
) {
  if (result?.result) {
    return result?.result.description;
  }
  if (result?.resultHla && result.resultHla.length > 0) {
    return result.resultHla.join(", ");
  }
  return result?.resultNumeric?.toString();
}

function findFirstInput(
  container: HTMLElement | null,
): HTMLElement | null | undefined {
  const el =
    container?.querySelector(
      ".result-item[data-state=open] input:not([type=hidden]), .result-item[data-state=open] textarea",
    ) || container?.querySelector("button[data-state=open]");
  return el as HTMLElement;
}

function usePrintReflexLabels() {
  const { printSpecimenLabels } = usePrintLabel();

  const suffixes = ["A", "B", "C", "D", "E", "F"];
  return async function printReflexLabels(sampleId: number, count: number) {
    const samples = await apiUtils.samples.forLabels.fetch({ ids: [sampleId] });
    if (!samples[0]) {
      return;
    }
    const sample = samples[0];
    for (let i = 0; i < count; i++) {
      const suffix = suffixes[i % suffixes.length];
      const displayId = `${sample.displayId ?? sample.accessionId.toString()}-${suffix}`;
      await printSpecimenLabels(sample.accession, [{ ...sample, displayId }]);
    }
  };
}
